import { Form, Input, Select, Card, Typography } from 'antd';
import { GoogleLogoIcon, OpenAiLogoIcon } from '@phosphor-icons/react';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { updateStylingWorkflowCreatePayload } from '../../../store/stylist/slice';
import {
  useGetImageModelsQuery,
  useGetOccasionsQuery,
  useSearchPromptTemplatesInfiniteQuery,
} from '../../../store/stylist/stylistApi';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

type WorkflowBasicInfoProps = {
  workflowGenre?: 'standard' | 'occasion';
};

const WorkflowBasicInfo = ({ workflowGenre = 'standard' }: WorkflowBasicInfoProps) => {
  const dispatch = useAppDispatch();
  const workflowPayload = useAppSelector(state => state.stylist.stylingWorkflowCreatePayload);

  const { data: promptTemplatesData, isLoading: promptTemplatesLoading } =
    useSearchPromptTemplatesInfiniteQuery({});

  const promptTemplates = promptTemplatesData?.pages?.flatMap(page => page) || [];
  const standardBasePromptTemplates = useMemo(
    () =>
      promptTemplates.filter(template => template.template_type === 'standard_workflow_prompt'),
    [promptTemplates],
  );
  const occasionBasePromptTemplates = useMemo(
    () =>
      promptTemplates.filter(template => template.template_type === 'occasion_workflow_prompt'),
    [promptTemplates],
  );
  const imagePromptTemplates = useMemo(
    () => promptTemplates.filter(template => template.template_type === 'image_generation_prompt'),
    [promptTemplates],
  );
  const { data: imageModelsData, isLoading: imageModelsLoading } = useGetImageModelsQuery();
  const { data: occasionTreeData, isLoading: occasionsLoading } = useGetOccasionsQuery();

  const imageModelOptions = useMemo(() => {
    if (imageModelsData?.models?.length) {
      return imageModelsData.models.map(model => ({
        value: model.litellm_model,
        label: `${model.display_name} (${model.litellm_model})`,
        provider: model.provider,
      }));
    }
    return [];
  }, [imageModelsData]);

  const occasionOptions = useMemo(
    () =>
      (occasionTreeData ?? []).map(occasion => ({
        value: occasion.id,
        label: occasion.name,
      })),
    [occasionTreeData],
  );

  const selectedOccasion = useMemo(
    () => (occasionTreeData ?? []).find(occasion => occasion.id === workflowPayload?.occasion_id),
    [occasionTreeData, workflowPayload?.occasion_id],
  );

  const subOccasionOptions = useMemo(
    () =>
      (selectedOccasion?.children ?? []).map(subOccasion => ({
        value: subOccasion.id,
        label: subOccasion.name,
      })),
    [selectedOccasion?.children],
  );

  useEffect(() => {
    if (workflowPayload?.workflow_genre !== workflowGenre) {
      dispatch(
        updateStylingWorkflowCreatePayload({
          workflow_genre: workflowGenre,
          ...(workflowGenre === 'standard'
            ? {
                occasion_id: undefined,
                sub_occasion_id: undefined,
                instructions: undefined,
                look_variants_required: undefined,
              }
            : {}),
        }),
      );
    }
  }, [dispatch, workflowGenre, workflowPayload?.workflow_genre]);

  useEffect(() => {
    if (!workflowPayload?.image_generation_model) {
      dispatch(
        updateStylingWorkflowCreatePayload({
          image_generation_model: imageModelsData?.default,
        }),
      );
    }
  }, [dispatch, workflowPayload?.image_generation_model, imageModelsData?.default]);

  const handleFieldChange = (field: string, value: string | number | undefined | null) => {
    dispatch(updateStylingWorkflowCreatePayload({ [field]: value }));
  };

  const handleOccasionChange = (occasionId?: number) => {
    dispatch(
      updateStylingWorkflowCreatePayload({
        occasion_id: occasionId,
        sub_occasion_id: undefined,
      }),
    );
  };

  return (
    <Card style={{ margin: '16px 0' }}>
      <Title level={4}>Workflow Information</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
        Provide basic information about your styling workflow
      </Text>

      <Form layout="vertical">
        <Form.Item
          label="Workflow Name"
          required
          rules={[{ required: true, message: 'Please enter workflow name' }]}
        >
          <Input
            placeholder="Enter workflow name"
            value={workflowPayload?.name || ''}
            onChange={e => handleFieldChange('name', e.target.value)}
          />
        </Form.Item>

        <Form.Item label="Description">
          <TextArea
            placeholder="Enter workflow description (optional)"
            rows={3}
            value={workflowPayload?.description || ''}
            onChange={e => handleFieldChange('description', e.target.value)}
          />
        </Form.Item>

        {workflowGenre === 'standard' && (
          <Form.Item
            label="Prompt Template"
            required
            rules={[{ required: true, message: 'Please select a prompt template' }]}
          >
            <Select
              placeholder="Select a prompt template"
              loading={promptTemplatesLoading}
              value={workflowPayload?.prompt_template}
              onChange={value => handleFieldChange('prompt_template', value)}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
              }
            >
              {standardBasePromptTemplates.map(template => (
                <Option key={template.id} value={template.id}>
                  {template.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {workflowGenre === 'occasion' && (
          <>
            <Form.Item
              label="Base Prompt Template"
              required
              rules={[{ required: true, message: 'Please select a base prompt template' }]}
            >
              <Select
                placeholder="Select base prompt template"
                loading={promptTemplatesLoading}
                value={workflowPayload?.prompt_template}
                onChange={value => handleFieldChange('prompt_template', value)}
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                }
              >
                {occasionBasePromptTemplates.map(template => (
                  <Option key={template.id} value={template.id}>
                    {template.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Image Prompt Template"
              required
              rules={[{ required: true, message: 'Please select an image prompt template' }]}
            >
              <Select
                placeholder="Select image prompt template"
                loading={promptTemplatesLoading}
                value={workflowPayload?.image_prompt_template}
                onChange={value => handleFieldChange('image_prompt_template', value)}
                showSearch
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                }
              >
                {imagePromptTemplates.map(template => (
                  <Option key={template.id} value={template.id}>
                    {template.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Occasion"
              required
              rules={[{ required: true, message: 'Please select an occasion' }]}
            >
              <Select
                placeholder="Select occasion"
                loading={occasionsLoading}
                options={occasionOptions}
                optionFilterProp="label"
                showSearch
                value={workflowPayload?.occasion_id}
                onChange={value => handleOccasionChange(value)}
              />
            </Form.Item>

            <Form.Item
              label="Sub-Occasion"
              required
              rules={[{ required: true, message: 'Please select a sub-occasion' }]}
            >
              <Select
                disabled={!workflowPayload?.occasion_id}
                placeholder="Select sub-occasion"
                options={subOccasionOptions}
                optionFilterProp="label"
                showSearch
                value={workflowPayload?.sub_occasion_id}
                onChange={value => handleFieldChange('sub_occasion_id', value)}
              />
            </Form.Item>

            <Form.Item label="Instructions">
              <TextArea
                placeholder="Add optional styling instructions"
                rows={3}
                value={workflowPayload?.instructions || ''}
                onChange={e => handleFieldChange('instructions', e.target.value)}
              />
            </Form.Item>
          </>
        )}

        <Form.Item
          label="Image Generation Model"
          required
          rules={[{ required: true, message: 'Please select an image model' }]}
        >
          <Select
            placeholder="Select an image model"
            loading={imageModelsLoading}
            value={workflowPayload?.image_generation_model || imageModelsData?.default}
            onChange={value => handleFieldChange('image_generation_model', value)}
          >
            {imageModelOptions.map(option => (
              <Option key={option.value} value={option.value}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span>{option.label}</span>
                  {option.provider === 'openai' && <OpenAiLogoIcon size={14} />}
                  {option.provider === 'gemini' && <GoogleLogoIcon size={14} />}
                </span>
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default WorkflowBasicInfo;
